import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Contact } from "@models/common/contact";
import { Relationship } from "@models/relationships/relationship";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { SelectOption } from "@models/ui-models/select-options";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import {
  CommonModalWrapperComponent
} from "@shared/components/commons/common-modal-wrapper/common-modal-wrapper.component";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import {
  customNicknameOption,
  EditContactModalComponent,
  ICommonContactFormControls
} from "@shared/components/contacts/edit-contact-modal/edit-contact-modal.component";
import { InputPhoneNumberComponent } from "@shared/controls/input-phone-number/input-phone-number.component";
import { InputComponent } from "@shared/controls/input/input.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { ContactsApiService } from "@shared/services/contacts-api.service";
import { RelationshipsApiService } from "@shared/services/relationships-api.service";
import { Guid } from "@shared/utils/guid.helper";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import { emailValidatorMsg } from '@shared/validators/custom-form-validators-messages';
import { ToastrService } from "ngx-toastr";
import { map, Observable, switchMap } from "rxjs";

interface IRelationshipContactFormControls extends ICommonContactFormControls {
  appointedOfficerId: FormControl<string | null>;

  // New Contact
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
}

export type IRelationshipContactFormGroup = FormGroup<IRelationshipContactFormControls>

@Component({
  selector: 'app-add-relationship-contact-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModalWrapperComponent,
    InputComponent,
    InputPhoneNumberComponent,
    SelectComponent,
    DividerComponent,
    ValidationErrorComponent
  ],
  providers: [],
  templateUrl: 'add-relationship-contact-modal.component.html',
})
export class AddRelationshipContactModalComponent implements OnInit {
  companiesApiService = inject(CompaniesApiService);
  relationshipsService = inject(RelationshipsApiService);
  contactsApiService = inject(ContactsApiService);
  activeModal = inject(NgbActiveModal);
  toastr = inject(ToastrService);
  destroyRef = inject(DestroyRef);

  @Input() entityId: string | null = null;
  @Input() allowAddNoRelationshipNewContact = false;
  @Input() newContactRelationshipId: string | null | undefined = null;

  @Output() _addNewContact = new EventEmitter<Contact>();
  @Output() _addNoRelationshipNewContact = new EventEmitter<Contact>();

  readonly emailValidatorMsg = emailValidatorMsg;
  readonly customNicknameOptionValue = customNicknameOption.value;
  readonly nicknameTypesOptions: SelectOption[] = [
    { label: 'Office', value: 'Office' },
    { label: 'Home', value: 'Home' },
    customNicknameOption
  ];
  readonly noRelationshipNewContact: SelectOption<string> = {
    label: 'New Contact',
    value: Guid.generate(),
  };
  readonly contactFormCustomErrors = EditContactModalComponent.contactFormCustomErrors;

  isLoading = signal<boolean>(false);

  relationships: Relationship[] = [];
  directorsOptions: SelectOption<string>[] = [];

  form: IRelationshipContactFormGroup = new FormGroup({
    appointedOfficerId: new FormControl<string | null>(null, [Validators.required]),
    email: new FormControl<string | null>(null, [CustomFormValidators.emailValidator]),
    phone: new FormControl<string | null>(null, [CustomFormValidators.phoneNumberValidator]),
    carbonCopy: new FormControl<string | null>(null, [CustomFormValidators.emailValidator]),
    blindCarbonCopy: new FormControl<string | null>(null, [CustomFormValidators.emailValidator]),
    nicknameType: new FormControl<string | null>(null, [Validators.required]),
    nickname: new FormControl<string | null>(null, [Validators.required]),

    // New Contact
    firstName: new FormControl<string | null>(null, Validators.required),
    lastName: new FormControl<string | null>(null, Validators.required),
  });

  ngOnInit(): void {
    this.fillDirectorsSelectOptions();
    this.defineContactRequiredFieldsValidation();

    if (this.newContactRelationshipId) {
      this.form.controls.appointedOfficerId.setValue(this.newContactRelationshipId);
    }
  }

  confirm(): void {
    if (this.form.invalid) {
      this.toastr.error('Please fill required fields', 'Error');
      this.form.markAllAsTouched();
      return;
    }

    const selectedRelationship = this.relationships.find((relationship) => relationship.relationshipId == this.form.controls.appointedOfficerId.value);
    const individualId = selectedRelationship?.individualId ?? Guid.EmptyGuid;
    const contact = new Contact({
      ...selectedRelationship?.individualDataOverride,
      individualId,
      ...this.form.value as Partial<Contact>,
      nickname: this.nickname
    });

    if (this.noRelationshipNewContactSelected) {
      contact.contactId = Guid.generate();
      this._addNoRelationshipNewContact.emit(contact);
      this.activeModal.close(contact);
      return;
    }

    this.setFormDisabled();
    this.contactsApiService.createOrUpdateContact(contact)
      .pipe(
        switchMap((contactId) => {
          contact.contactId = contactId;
          const request = {
            contactId,
            individualId,
            relationshipsIdToUpdate: [selectedRelationship?.relationshipId].filter(Boolean) as string[]
          };

          return this.relationshipsService.setRelationshipsContacts(request).pipe(map(() => contact));
        }),
      )
      .subscribe({
        next: (contact) => {
          this._addNewContact.emit(contact);
          this.activeModal.close(contact);
        },
        error: (error) => {
          console.error('[AddRelationshipContactModalComponent.createOrUpdateContact] error: ', error);
          this.setFormDisabled(false);
          this.toastr.error('Failed to add contact.', 'Error');
        }
      });
  }

  close(): void {
    this.activeModal.dismiss();
  }

  onNicknameChanged(option: SelectOption | null): void {
    const nicknameType = option?.value;
    setControlDisabled(this.form.controls.nickname, nicknameType !== this.customNicknameOptionValue);
  }

  fillDirectorsSelectOptions(): void {
    if (!this.entityId) {
      this.activeModal.dismiss();
      return;
    }

    this.isLoading.set(true);
    this.companiesApiService.getOfficeholders(this.entityId)
      .subscribe({
        next: (relationships) => {
          const options: SelectOption<string>[] = [];

          this.relationships = relationships
            .filter(({ relationship }) => relationship.type == RelationshipType.Director && !relationship.end)
            .map(({ relationship }) => {
              options.push({
                label: `${ relationship.individualDataOverride?.fullNameWithMiddle } (Director)`,
                value: relationship.relationshipId
              });
              return relationship;
            });
          this.directorsOptions = options;

          if (this.allowAddNoRelationshipNewContact) {
            this.directorsOptions.push(this.noRelationshipNewContact);
          }

          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('[AddRelationshipContactModalComponent.fillDirectorsSelectOptions] error: ', error);
          this.toastr.error('Failed load directors.', 'Error');
          this.activeModal.dismiss();
        }
      });
  }

  onAppointedOfficerIdSelected(option: SelectOption | null): void {
    if (!this.allowAddNoRelationshipNewContact || !option) {
      setControlDisabled(this.form.controls.firstName);
      setControlDisabled(this.form.controls.lastName);
      return;
    }

    const contactId = option.value as string;
    const isNewContactSelected = contactId == this.noRelationshipNewContact.value;

    setControlDisabled(this.form.controls.firstName, !isNewContactSelected);
    setControlDisabled(this.form.controls.lastName, !isNewContactSelected);
  }

  setFormDisabled(isDisabled = true): void {
    this.isLoading.set(isDisabled);

    setControlDisabled(this.form, isDisabled);
    setControlDisabled(this.form.controls.nickname, isDisabled || this.form.controls.nicknameType.value !== customNicknameOption.value);

    if (!isDisabled && !this.noRelationshipNewContactSelected) {
      setControlDisabled(this.form.controls.firstName);
      setControlDisabled(this.form.controls.lastName);
    }
  }

  defineContactRequiredFieldsValidation(): void {
    EditContactModalComponent.defineContactRequiredFieldsValidation(this.form);
  }

  get nickname(): string {
    if (this.form.controls.nicknameType.value !== customNicknameOption.value) {
      return this.form.controls.nicknameType.value!;
    }

    return this.form.controls.nickname.value!;
  }

  get noRelationshipNewContactSelected(): boolean {
    return this.allowAddNoRelationshipNewContact && this.form.controls.appointedOfficerId.value == this.noRelationshipNewContact.value;
  }

  get addNewContact(): Observable<Contact> {
    return this._addNewContact.asObservable().pipe(takeUntilDestroyed(this.destroyRef));
  }

  get addNoRelationshipNewContact(): Observable<Contact> {
    return this._addNoRelationshipNewContact.asObservable().pipe(takeUntilDestroyed(this.destroyRef));
  }
}
